import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import {useDataStore} from "@/stores/dataStore.js";

export const useCartStore = defineStore('cart', () => {
    // Cart state
    const isOpen = ref(false);
    const items = ref([]);

    // Load cart data from localStorage on initialization
    const initializeCart = () => {
        const savedCart = localStorage.getItem('eskragCart');
        if (savedCart) {
            try {
                const parsed = JSON.parse(savedCart);
                if (Array.isArray(parsed)) {
                    items.value = parsed.map(item => {
                        const rawId = item.originalId || (typeof item.id === 'string' ? item.id.split('-')[0] : item.id);
                        let price = parseFloat(item.price);
                        let origPrice = parseFloat(item.originalPrice);
                        if (isNaN(price) || price <= 0) {
                            price = !isNaN(origPrice) && origPrice > 0 ? origPrice : 0;
                        }
                        return {
                            ...item,
                            originalId: item.originalId || rawId,
                            price: !isNaN(price) && price >= 0 ? price : 0,
                            originalPrice: !isNaN(origPrice) && origPrice >= 0 ? origPrice : price
                        };
                    });
                } else {
                    items.value = [];
                }
            } catch (e) {
                console.error('Failed to parse saved cart:', e);
                items.value = [];
            }
        }
    };

    // Call initialize immediately
    initializeCart();

    // Watch for cart changes and save to localStorage
    watch(
        items,
        (newItems) => {
            localStorage.setItem('eskragCart', JSON.stringify(newItems));

        },
        { deep: true }
    );

    // Computed properties
    const count = computed(() => {
        return items.value.reduce((total, item) => total + (parseInt(item.quantity, 10) || 1), 0);
    });

    const total = computed(() => {
        const sum = items.value.reduce((total, item) => {
            const price = parseFloat(item.price ?? item.originalPrice ?? 0);
            const qty = parseInt(item.quantity, 10);
            const validPrice = !isNaN(price) && price >= 0 ? price : 0;
            const validQty = !isNaN(qty) && qty > 0 ? qty : 1;
            return total + (validPrice * validQty);
        }, 0);
        return sum;
    });

    const isEmpty = computed(() => items.value.length === 0);

    // Methods
    const toggleCart = () => {
        isOpen.value = !isOpen.value;
        console.log('Cart toggled:', isOpen.value);
    };

    const addItem = (product) => {
        // Check if product already exists in cart
        const existingItem = items.value.find(item => item.id === product.id);
        const parsedPrice = parseFloat(product.price ?? product.originalPrice ?? product.prix ?? 0);
        const cleanPrice = !isNaN(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;
        const rawId = product.originalId || (typeof product.id === 'string' ? product.id.split('-')[0] : product.id);

        if (existingItem) {
            // If it's the same product with the same custom fields, increase quantity
            if (
                (existingItem.playerInfo === product.playerInfo || (!existingItem.playerInfo && !product.playerInfo)) &&
                (existingItem.amount === product.amount || (!existingItem.amount && !product.amount)) &&
                (existingItem.amount2 === product.amount2 || (!existingItem.amount2 && !product.amount2))
            ) {
                existingItem.quantity += (parseInt(product.quantity, 10) || 1);
            } else {
                // If it's the same product ID but with different custom fields, add as new item
                // Add a unique identifier to avoid collision
                const uniqueId = `${product.id}-${Date.now()}`;
                items.value.push({
                    ...product,
                    id: uniqueId,
                    originalId: rawId,
                    price: cleanPrice,
                    originalPrice: cleanPrice
                });
            }
        } else {
            console.log('Adding new product to cart:', product);
            items.value.push({
                ...product,
                originalId: rawId,
                price: cleanPrice,
                originalPrice: cleanPrice
            });
        }
    };

    const updateQuantity = (id, newQuantity) => {
        const item = items.value.find(item => item.id === id);
        if (item) {
            item.quantity = Math.max(1, parseInt(newQuantity, 10) || 1);
        }
    };

    const removeItem = (id) => {
        items.value = items.value.filter(item => item.id !== id);
    };

    const clearCart = () => {
        items.value = [];
    };

    // Force save cart to localStorage
    const saveCart = () => {
        localStorage.setItem('eskragCart', JSON.stringify(items.value));
    };

    const recalculatePrices = () => {
        const datastore = useDataStore();
        if (!datastore.articles || !datastore.articles.length) {
            return;
        }

        function fetchUpdatedPrice(item) {
            const rawId = item.originalId || (typeof item.id === 'string' ? item.id.split('-')[0] : item.id);
            const found = datastore.articles.find(article =>
                article.id?.toString() === rawId?.toString() ||
                article.id?.toString() === item.id?.toString()
            );
            if (!found) return null;
            const rawPrix = found.prix ?? found.price;
            if (rawPrix === undefined || rawPrix === null) return null;
            const converted = datastore.convertPrice(rawPrix, found);
            const num = parseFloat(converted);
            return !isNaN(num) && num > 0 ? num : parseFloat(rawPrix);
        }

        items.value = items.value.map(item => {
            const rawId = item.originalId || (typeof item.id === 'string' ? item.id.split('-')[0] : item.id);
            const baseOriginalId = item.originalId || rawId;

            // Never overwrite custom prices (e.g. calculated from amount / slider)
            if (item.is_custom_price) {
                const currentPrice = parseFloat(item.price);
                return {
                    ...item,
                    originalId: baseOriginalId,
                    price: !isNaN(currentPrice) && currentPrice >= 0 ? currentPrice : 0
                };
            }

            const updatedPrice = fetchUpdatedPrice(item);
            if (updatedPrice !== null && !isNaN(updatedPrice) && updatedPrice > 0) {
                return {
                    ...item,
                    originalId: baseOriginalId,
                    price: updatedPrice,
                    originalPrice: updatedPrice
                };
            }

            // Preserve existing price if lookup did not find a valid replacement
            let currentPrice = parseFloat(item.price);
            if (isNaN(currentPrice) || currentPrice <= 0) {
                currentPrice = parseFloat(item.originalPrice);
            }

            return {
                ...item,
                originalId: baseOriginalId,
                price: !isNaN(currentPrice) && currentPrice >= 0 ? currentPrice : 0
            };
        });
    };


    return {
        isOpen,
        items,
        count,
        total,
        isEmpty,
        toggleCart,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        saveCart,
        recalculatePrices
    };
});